import type { FiatFlow } from '@funkit/connect-core'
import type { TFunction } from 'i18next'
import { useEffect, useMemo, useRef } from 'react'

import type {
  DepositFlowCallbacks,
  DepositMethod,
} from '../flow/depositFlowConfig'
import { type FunkitFlowError, flowError } from '../flow/flowError'
import { logger } from '../logging/logger'

/** Reports a deposit's outcome to the host once, whichever screen reaches it first. */
export interface DepositOutcomeReporter {
  /** The order exists: the user leaving from here on is not a cancellation. */
  ordered: () => void
  /**
   * The deposit is on its way and the flow has closed behind it. Only the
   * settle path calls this; see `onDepositSubmitted` for what it promises.
   */
  submitted: (orderId: string, method: DepositMethod['type']) => void
  failed: (error: FunkitFlowError) => void
}

type StepState = NonNullable<FiatFlow['stepResponse']>['state']

const ORDERED_SCREENS = new Set<FiatFlow['page']['screen']>([
  'ORDER_REVIEW',
  'ORDER_PENDING',
  'ORDER_TRACKING',
  'SUCCESS',
])

/**
 * Lives above the flow session, which remounts on "back to entry": a report
 * belongs to the deposit, not to one session of it. Unmounting before any
 * report means the user left, and the host hears that as `cancelled`.
 */
export function useDepositOutcomeReporter({
  callbacks,
  checkoutId,
  paymentMethod,
  t,
}: {
  callbacks: Pick<DepositFlowCallbacks, 'onDepositSubmitted' | 'onDepositError'>
  checkoutId: string
  paymentMethod: string
  t: TFunction
}): DepositOutcomeReporter {
  const reported = useRef(false)
  const submitted = useRef(false)
  const effectGeneration = useRef(0)
  // Read at call time: the host may hand a fresh callbacks object per render.
  const latest = useRef({ callbacks, t })
  latest.current = { callbacks, t }

  const reporter = useMemo<DepositOutcomeReporter>(
    () => ({
      ordered() {
        reported.current = true
      },
      submitted(orderId, method) {
        if (submitted.current) {
          return
        }
        submitted.current = true
        reported.current = true
        logger.info('connect-rn:deposit:submitted', {
          checkoutId,
          orderId,
          paymentMethod,
        })
        latest.current.callbacks.onDepositSubmitted?.(orderId, method)
      },
      failed(error) {
        reported.current = true
        logger.warn('connect-rn:deposit:failed', {
          checkoutId,
          code: error.code,
          isRetryable: error.isRetryable,
          isFatal: error.isFatal,
        })
        latest.current.callbacks.onDepositError?.(error)
      },
    }),
    [checkoutId, paymentMethod],
  )

  useEffect(() => {
    const generation = effectGeneration.current + 1
    effectGeneration.current = generation
    return () => {
      // Strict Mode sets the effect up again in this microtask window; a real
      // unmount does not, so only the latter reports cancellation.
      queueMicrotask(() => {
        if (effectGeneration.current !== generation || reported.current) {
          return
        }
        logger.info('connect-rn:deposit:cancelled', { checkoutId })
        latest.current.callbacks.onDepositError?.(
          flowError('cancelled', latest.current.t('fiat.cancelled')),
        )
      })
    }
  }, [checkoutId])

  return reporter
}

/** Reads the flow's page and tells the reporter when it reaches an outcome. */
export function useDepositOutcome(
  flow: FiatFlow,
  reporter: DepositOutcomeReporter,
  t: TFunction,
): void {
  const { screen, identity } = flow.page
  const state = flow.stepResponse?.state
  useEffect(() => {
    if (ORDERED_SCREENS.has(screen)) {
      reporter.ordered()
    }
    const error = terminalError(screen, state, t)
    if (error !== undefined) {
      reporter.failed(error)
    }
    // Keyed on the page, not the render: a re-render of the same state is not
    // a second outcome. The screen rides along because an order keeps its
    // identity as it fails, and that failure is the host's to hear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, screen, reporter])
}

/** The host-facing error a terminal page stands for, or nothing while the flow is live. */
export function terminalError(
  screen: FiatFlow['page']['screen'],
  state: StepState | undefined,
  t: TFunction,
): FunkitFlowError | undefined {
  switch (screen) {
    case 'FAILURE':
      return failureError(state, t)
    case 'REFUND':
      return flowError('unknown', t('fiat.refunded'))
    case 'BLOCKED':
      return blockedError(state, t)
    default:
      return undefined
  }
}

function failureError(
  state: StepState | undefined,
  t: TFunction,
): FunkitFlowError {
  if (state?.kind !== 'ORDER' || state.phase !== 'CREATED') {
    return flowError('unknown', t('fiat.failure'))
  }
  if (state.status === 'CANCELLED') {
    return flowError('cancelled', t('fiat.cancelled'))
  }
  const reason = state.failureReason ?? state.error
  if (reason?.category === 'PAYMENT_DECLINED') {
    return flowError('payment_declined', t('fiat.failure'), {
      isRetryable: reason.retryable,
    })
  }
  return flowError('unknown', t('fiat.failure'), {
    isRetryable: reason?.retryable ?? false,
  })
}

function blockedError(
  state: StepState | undefined,
  t: TFunction,
): FunkitFlowError {
  if (state?.kind !== 'BLOCKED') {
    return flowError('blocked', t('fiat.blocked'))
  }
  if (state.reason === 'ALL_PROVIDERS_DECLINED') {
    return flowError('payment_declined', t('fiat.refusalUnavailableTitle'))
  }
  // A comeback date makes the block a wait, not a verdict.
  const isRetryable =
    state.retryAfter !== undefined && state.retryAfter !== null
  return flowError('blocked', t('fiat.blocked'), {
    isRetryable,
    isFatal: !isRetryable,
  })
}
